import React, { useState, useEffect } from 'react';
import UsersService, { BOAPI } from '../service/UsersService';
import { useNavigate } from "react-router-dom";
import { userDisplayName, userSecondaryName } from '../utils/userName';

// כל משתמש שנרשם (SignUp) מופעל מיידית (isActive=1) - אין קונספט של "ממתין
// לאישור אדמין", אז המסך כאן פשוט: טבלת כל המשתמשים. תפקיד (user/owner/
// admin) נערך מתוך כרטיס המשתמש (UserDetails.jsx), לא כאן
const UsersTab = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // יצירת משתמש חדש (BOCreateUser) - created = התוצאה, כולל הסיסמה (מוצגת פעם אחת)
  const emptyNewUser = { phone: '', fName: '', lName: '', email: '', role: 'customer', randomPassword: true, password: '' };
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await UsersService.getUsers(async () => {
        const resp = await BOAPI.fetchUsers();
        if (resp?.[0]?.[0]?.code === 0) return resp[1] || [];
        throw new Error(resp?.[0]?.[0]?.msg || 'שגיאה בטעינה');
      });
      setUsers(data);
    } catch {
      setError('שגיאה בטעינת המשתמשים');
    } finally {
      setLoading(false);
    }
  };

  const setField = (key) => (e) => setNewUser((p) => ({ ...p, [key]: e.target.value }));

  const closeCreate = () => {
    setCreating(false);
    setNewUser(emptyNewUser);
    setCreateError('');
    setCreated(null);
    setCopied(false);
  };

  const createUser = async () => {
    const phone = newUser.phone.replace(/\D/g, '');
    if (phone.length < 9 || phone.length > 11) return setCreateError('מספר טלפון לא תקין - ספרות בלבד, בין 9 ל-11 ספרות');
    if (!newUser.fName.trim()) return setCreateError('יש להזין שם פרטי');
    if (!newUser.randomPassword && newUser.password.trim().length < 6) return setCreateError('הסיסמה חייבת להכיל לפחות 6 תווים');
    setCreateBusy(true);
    setCreateError('');
    try {
      const resp = await BOAPI.createUser({
        phone,
        fName: newUser.fName.trim(),
        lName: newUser.lName.trim(),
        email: newUser.email.trim() || undefined,
        role: newUser.role,
        ...(newUser.randomPassword ? { generatePassword: true } : { password: newUser.password.trim() }),
      });
      if (resp?.[0]?.[0]?.code === 0) {
        setCreated(resp[1]?.[0] || null);
        // רענון הטבלה (המטמון של UsersService מתאפס)
        UsersService.clear();
        loadUsers();
      } else {
        setCreateError(resp?.[0]?.[0]?.msg || 'יצירת המשתמש נכשלה');
      }
    } catch {
      setCreateError('שגיאה בתקשורת עם השרת');
    } finally {
      setCreateBusy(false);
    }
  };

  const sendCreatedWhatsApp = () => {
    const digits = String(created.phone || '').replace(/[^0-9]/g, '');
    const intl = digits.startsWith('972') ? digits : digits.startsWith('0') ? `972${digits.slice(1)}` : digits;
    const text = `שלום${created.fName ? ` ${created.fName}` : ''}, נפתח לך חשבון באפליקציית איגוד - מועדון ההטבות.\nמספר טלפון להתחברות: ${created.phone}\nסיסמה: ${created.password}\nלהורדה ולכניסה: https://e-gud.com`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  const filteredUsers = search.trim()
    ? users.filter((u) => {
      const q = search.trim().toLowerCase();
      const businessNames = [...(u.ownedBusinesses || []), ...(u.staffBusinesses || [])].map((b) => b.name);
      return [u.fName, u.lName, u.nickname, u.email, u.phone, ...businessNames].some((v) => (v || '').toLowerCase().includes(q));
    })
    : users;

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>ניהול משתמשים</h2>
        <div style={s.headerActions}>
          <span style={s.totalBadge}>{users.length} משתמשים</span>
          <button style={s.addBtn} onClick={() => setCreating(true)}>+ משתמש חדש</button>
        </div>
      </div>

      {creating && (
        <div style={s.overlay} onClick={() => !createBusy && closeCreate()}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.modalTitle}>משתמש חדש</h3>
            {!created ? (
              <>
                <label style={s.label}>מספר טלפון * <span style={s.hint}>(גם שם ההתחברות)</span></label>
                <input style={s.input} dir="ltr" type="tel" value={newUser.phone} onChange={(e) => setNewUser((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))} placeholder="0501234567" />
                <div style={s.twoCols}>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>שם פרטי *</label>
                    <input style={s.input} value={newUser.fName} onChange={setField('fName')} maxLength={32} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>שם משפחה</label>
                    <input style={s.input} value={newUser.lName} onChange={setField('lName')} maxLength={32} />
                  </div>
                </div>
                <label style={s.label}>אימייל</label>
                <input style={s.input} dir="ltr" type="email" value={newUser.email} onChange={setField('email')} />

                <label style={s.label}>סוג משתמש</label>
                <div style={s.segment}>
                  {[{ key: 'customer', label: 'לקוח' }, { key: 'owner', label: 'בעל עסק' }].map((o) => (
                    <button key={o.key} type="button" style={{ ...s.segmentBtn, ...(newUser.role === o.key ? s.segmentBtnActive : {}) }} onClick={() => setNewUser((p) => ({ ...p, role: o.key }))}>
                      {o.label}
                    </button>
                  ))}
                </div>

                <label style={s.label}>סיסמה</label>
                <label style={s.radioRow}>
                  <input type="radio" checked={newUser.randomPassword} onChange={() => setNewUser((p) => ({ ...p, randomPassword: true }))} />
                  <span>סיסמה אקראית (8 תווים, קלה להקלדה)</span>
                </label>
                <label style={s.radioRow}>
                  <input type="radio" checked={!newUser.randomPassword} onChange={() => setNewUser((p) => ({ ...p, randomPassword: false }))} />
                  <span>אקליד סיסמה בעצמי</span>
                </label>
                {!newUser.randomPassword && (
                  <input style={s.input} dir="ltr" type="text" value={newUser.password} onChange={setField('password')} placeholder="לפחות 6 תווים" />
                )}

                {createError && <div style={s.modalError}>❌ {createError}</div>}
                <div style={s.modalActions}>
                  <button style={s.cancelBtn} onClick={closeCreate} disabled={createBusy}>ביטול</button>
                  <button style={s.primaryBtn} onClick={createUser} disabled={createBusy}>{createBusy ? 'יוצר...' : 'יצירת משתמש'}</button>
                </div>
              </>
            ) : (
              <>
                <p style={s.modalText}>✅ המשתמש <strong>{[created.fName, created.lName].filter(Boolean).join(' ')}</strong> נוצר. העבירו לו את פרטי ההתחברות - הסיסמה מוצגת פעם אחת בלבד.</p>
                <div style={s.credBox}>
                  <div>טלפון: <strong dir="ltr">{created.phone}</strong></div>
                  <div>סיסמה: <strong dir="ltr" style={s.credPassword}>{created.password}</strong></div>
                </div>
                <div style={s.modalActions}>
                  <button style={s.cancelBtn} onClick={() => { navigator.clipboard?.writeText(`טלפון: ${created.phone}\nסיסמה: ${created.password}`); setCopied(true); }}>
                    {copied ? '✓ הועתק' : '📋 העתקה'}
                  </button>
                  <button style={s.cancelBtn} onClick={sendCreatedWhatsApp}>💬 שליחה בוואטסאפ</button>
                  <button style={s.primaryBtn} onClick={() => { const id = created.id; closeCreate(); navigate(`/users/${id}`); }}>לדף המשתמש</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}

      <input
        type="text"
        placeholder="חיפוש לפי שם, אימייל או טלפון..."
        style={s.searchInput}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <div style={s.center}>טוען משתמשים...</div>
      ) : filteredUsers.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>👤</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>לא נמצאו משתמשים</p>
        </div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow2}>
                <th style={s.th}>ID</th>
                <th style={s.th}>טלפון</th>
                <th style={s.th}>שם</th>
                <th style={s.th}>בית עסק</th>
                <th style={s.th}>אימייל</th>
                <th style={s.th}>תפקיד</th>
                <th style={s.th}>סטטוס</th>
                <th style={s.th}>נרשם</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr
                  key={user.id}
                  style={s.row}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                >
                  <td style={s.td}><span style={s.idText}>{user.id}</span></td>
                  <td style={s.td}><span style={s.phoneText}>{user.phone || '-'}</span></td>
                  <td style={s.td}>
                    <button
                      style={s.nameLink}
                      onClick={() => navigate(`/users/${user.id}`)}
                    >
                      {userDisplayName(user)}
                    </button>
                    {userSecondaryName(user) && <div style={s.secondaryName}>{userSecondaryName(user)}</div>}
                  </td>
                  {/* בתי עסק שבבעלותו (מודגש) + שבהם הוא עובד/מנהל - לחיצה פותחת את דף בית העסק */}
                  <td style={s.td}>
                    {(user.ownedBusinesses?.length || user.staffBusinesses?.length) ? (
                      <div style={s.businessList}>
                        {(user.ownedBusinesses || []).map((b) => (
                          <button key={`o${b.id}`} style={s.businessLink} onClick={() => navigate(`/businesses/${b.id}`)}>🏪 {b.name}</button>
                        ))}
                        {(user.staffBusinesses || []).map((b) => (
                          <button key={`s${b.id}`} style={{ ...s.businessLink, fontWeight: 500 }} onClick={() => navigate(`/businesses/${b.id}`)}>
                            {b.name} <span style={s.secondaryName}>({b.role === 'manager' ? 'מנהל' : 'עובד'})</span>
                          </button>
                        ))}
                      </div>
                    ) : '-'}
                  </td>
                  <td style={s.td}>{user.email || '-'}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.roleBadge,
                      background: user.role === 'admin' ? '#FEF9EE' : '#EEF2FF',
                      color: user.role === 'admin' ? '#C9A84C' : '#1f6feb',
                    }}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {/* pendingInvite - החשבון נוצר ע"י הצוות/בעל עסק ועוד לא התחבר אף פעם */}
                    {user.isActive && user.pendingInvite ? (
                      <span style={{ ...s.statusBadge, ...s.statusInvited }} title="החשבון נוצר עבורו, אבל הוא עוד לא התחבר לאפליקציה">
                        הוזמן - טרם התחבר
                      </span>
                    ) : (
                      <span style={{ ...s.statusBadge, ...(user.isActive ? s.statusActive : s.statusInactive) }}>
                        {user.isActive ? 'פעיל' : 'לא פעיל'}
                      </span>
                    )}
                  </td>
                  <td style={s.td}>{fmtDate(user.iTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  headerActions:{ display: 'flex', alignItems: 'center', gap: 10 },
  addBtn:       { padding: '9px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal:        { background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  modalTitle:   { margin: '0 0 16px', fontSize: 19, color: '#1a1f36' },
  modalText:    { fontSize: 14, color: '#4f566b', lineHeight: 1.6, margin: '0 0 14px' },
  modalError:   { color: '#c62828', fontSize: 13, fontWeight: 600, margin: '4px 0 12px' },
  modalActions: { display: 'flex', gap: 10, marginTop: 8 },
  label:        { display: 'block', fontSize: 12.5, fontWeight: 700, color: '#4f566b', marginBottom: 6 },
  hint:         { fontWeight: 500, color: '#8792a2' },
  input:        { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, marginBottom: 14, fontFamily: 'inherit' },
  twoCols:      { display: 'flex', gap: 10 },
  segment:      { display: 'flex', gap: 8, marginBottom: 14 },
  segmentBtn:   { flex: 1, padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#697386' },
  segmentBtnActive: { background: '#1f6feb', borderColor: '#1f6feb', color: '#fff' },
  radioRow:     { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#1a1f36', marginBottom: 8, cursor: 'pointer' },
  cancelBtn:    { flex: 1, padding: '11px 14px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 },
  primaryBtn:   { flex: 1, padding: '11px 14px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 },
  credBox:      { background: '#f6f8fa', borderRadius: 10, padding: 14, fontSize: 15, lineHeight: 1.9, marginBottom: 14 },
  credPassword: { fontFamily: 'monospace', fontSize: 18, letterSpacing: 1.5 },
  totalBadge:   { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },
  searchInput:  { width: '100%', maxWidth: 360, padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13, marginBottom: 18, boxSizing: 'border-box' },

  tableWrapper: { overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow2:   { backgroundColor: '#f5f7fa', borderBottom: '2px solid #e3e8ee' },
  th:           { padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 13, borderBottom: '2px solid #e3e8ee' },
  row:          { borderBottom: '1px solid #f0f2f5', cursor: 'pointer', transition: 'background 0.15s' },
  td:           { padding: '14px 16px', color: '#4f566b', fontSize: 13, textAlign: 'right' },
  idText:       { fontWeight: 700, color: '#a3acb9' },
  phoneText:    { fontWeight: 700, color: '#1a1f36', direction: 'ltr', unicodeBidi: 'embed' },
  secondaryName:{ color: '#8792a2', fontSize: 11.5, marginTop: 2 },
  nameLink:     { background: 'none', border: 'none', cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 13, padding: 0 },
  roleBadge:    { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusBadge:  { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32' },
  statusInactive: { background: '#f4f6f8', color: '#697386' },
  statusInvited: { background: '#fff8e1', color: '#a06d00', whiteSpace: 'nowrap' },
  businessList: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 },
  businessLink: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 13, textAlign: 'right' },
};

export default UsersTab;
